
import React, { useState } from "react";
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarClock, Plus, Search, Filter, Pencil, Trash2, CheckCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { format } from "date-fns";
import { useTasks } from "@/hooks/useTasks";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useNavigate } from "react-router-dom";
import { ActionMenu, ActionItem } from "@/components/ui/action-menu";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";

export default function Tasks() {
  const { tasks, isLoading, completeTask, deleteTask } = useTasks();
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const navigate = useNavigate();
  
  // Filter tasks based on search and status
  const filteredTasks = tasks
    .filter(task => {
      if (filter === "all") return true;
      if (filter === "pending") return task.status === "pending";
      if (filter === "completed") return task.status === "completed";
      if (filter === "overdue") {
        return task.status === "pending" && 
          task.due_date && new Date(task.due_date) < new Date();
      }
      return true;
    })
    .filter(task => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        task.title?.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query) ||
        task.organization?.name?.toLowerCase().includes(query) ||
        (task.contact && 
          `${task.contact.first_name} ${task.contact.last_name}`.toLowerCase().includes(query))
      );
    });

  const handleComplete = (id: string) => {
    completeTask(id);
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Badge className="bg-red-100 text-red-700">High</Badge>;
      case 'medium':
        return <Badge className="bg-amber-100 text-amber-700">Medium</Badge>;
      case 'low':
        return <Badge className="bg-green-100 text-green-700">Low</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Normal</Badge>;
    }
  };

  const getStatusBadge = (status: string, dueDate: string | null) => {
    if (status === 'completed') {
      return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
    }
    
    if (dueDate && new Date(dueDate) < new Date()) {
      return <Badge variant="destructive">Overdue</Badge>;
    }
    
    return <Badge className="bg-blue-100 text-blue-700">Pending</Badge>;
  };

  const formatDueDate = (dueDate: string | null) => {
    if (!dueDate) return "No due date";
    
    const date = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }
    
    if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    }
    
    return format(date, "MMM d, yyyy");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground">
            Manage your tasks and follow-ups
          </p>
        </div>
        <Button onClick={() => navigate("/tasks/create")}>
          <Plus className="mr-2 h-4 w-4" /> Create Task
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle>Task List</CardTitle>
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search tasks..."
                className="pl-8 w-full sm:w-[300px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <Tabs defaultValue="all" onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="overdue">Overdue</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <CalendarClock className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold">
                  {searchQuery 
                    ? "No tasks match your search" 
                    : filter === "pending" 
                      ? "No pending tasks"
                      : filter === "completed"
                        ? "No completed tasks yet"
                        : filter === "overdue"
                          ? "No overdue tasks"
                          : "No tasks yet"}
                </h3>
                <p className="text-muted-foreground text-sm max-w-md">
                  {searchQuery 
                    ? "Try adjusting your search terms or clearing filters to see more results."
                    : filter === "pending"
                      ? "All caught up! No pending tasks at the moment."
                      : filter === "completed"
                        ? "Complete some tasks to see them here."
                        : filter === "overdue"
                          ? "Great job staying on top of your deadlines!"
                          : "Stay organized by creating tasks for your follow-ups, deadlines, and important reminders."}
                </p>
              </div>
              {!searchQuery && filter === "all" && (
                <Button onClick={() => navigate("/tasks/create")} size="lg" className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Task
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">Status</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Related To</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>
                        <Checkbox
                          checked={task.status === 'completed'}
                          disabled={task.status === 'completed'}
                          onCheckedChange={() => handleComplete(task.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{task.title}</div>
                        {task.description && (
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {task.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {task.organization ? (
                          <div>
                            <div className="font-medium">{task.organization.name}</div>
                            <div className="text-xs text-muted-foreground">Organization</div>
                          </div>
                        ) : task.contact ? (
                          <div>
                            <div className="font-medium">{`${task.contact.first_name} ${task.contact.last_name}`}</div>
                            <div className="text-xs text-muted-foreground">Contact</div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDueDate(task.due_date)}
                      </TableCell>
                      <TableCell>
                        {getPriorityBadge(task.priority)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(task.status, task.due_date)}
                      </TableCell>
                      <TableCell className="text-right">
                        <ActionMenu
                          items={[
                            {
                              label: "Mark Complete",
                              icon: <CheckCircle className="h-4 w-4 mr-2" />,
                              onClick: () => handleComplete(task.id),
                              ...(task.status === 'completed' && {
                                onClick: undefined, // Disable the action if already completed
                              }),
                            },
                            {
                              label: "Edit",
                              icon: <Pencil className="h-4 w-4 mr-2" />,
                              href: `/tasks/${task.id}/edit`,
                            },
                            {
                              label: "Delete",
                              icon: <Trash2 className="h-4 w-4 mr-2" />,
                              onClick: () => setTaskToDelete(task.id),
                              variant: "destructive",
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        isOpen={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={async () => {
          if (taskToDelete) {
            await deleteTask(taskToDelete);
          }
        }}
        entityName="task"
      />
    </div>
  );
}
